'use client'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import * as React from 'react'
import { cm } from '~/utils/utils'
import { SelectComponentRightCaretIcon } from '../../icons'
import Typography from '../Typography'

// Context for sharing trigger ref
const DropdownMenuContext = React.createContext<{
  triggerRef?: React.RefObject<HTMLButtonElement>
}>({})

// Main DropdownMenu component
interface DropdownMenuProps extends React.ComponentProps<typeof DropdownMenuPrimitive.Root> {
  children: React.ReactNode
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, ...props }) => {
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  return (
    <DropdownMenuContext.Provider value={{ triggerRef }}>
      <DropdownMenuPrimitive.Root {...props}>{children}</DropdownMenuPrimitive.Root>
    </DropdownMenuContext.Provider>
  )
}

// Trigger component
interface DropdownMenuTriggerProps {
  children: React.ReactNode
  className?: string
  asChild?: boolean
}

export const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  DropdownMenuTriggerProps
>(({ children, className, asChild = false, ...props }, ref) => {
  const { triggerRef } = React.useContext(DropdownMenuContext)

  return (
    <DropdownMenuPrimitive.Trigger
      ref={node => {
        if (triggerRef && 'current' in triggerRef) {
          ;(triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
        }
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref && 'current' in ref) {
          ;(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
        }
      }}
      asChild={asChild}
      className={className}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Trigger>
  )
})

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger'

// Content component with your Figma styles
interface DropdownMenuContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>, 'children'> {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  alignOffset?: number
  /** Minimum space from viewport edges when shifting/flipping (Radix / Floating UI). */
  collisionPadding?: number
  /**
   * When false, keeps the menu on the chosen `side` with full `sideOffset` instead of
   * flipping/shifting into the trigger (fixes “flush” menus near scroll/clipping regions).
   */
  avoidCollisions?: boolean
  matchTriggerWidth?: boolean
}

export const DROPDOWN_MENU_SIDE_OFFSET = 8

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(
  (
    {
      children,
      className = '',
      style,
      align = 'start',
      side = 'bottom',
      sideOffset = DROPDOWN_MENU_SIDE_OFFSET,
      alignOffset = 0,
      collisionPadding,
      avoidCollisions = true,
      matchTriggerWidth = false,
      ...props
    },
    ref
  ) => {
    const { triggerRef } = React.useContext(DropdownMenuContext)
    const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>()

    React.useEffect(() => {
      if (matchTriggerWidth && triggerRef?.current) {
        const updateWidth = () => {
          const width = triggerRef.current?.getBoundingClientRect().width
          if (width) {
            setTriggerWidth(width)
          }
        }

        // Initial calculation
        updateWidth()

        // Listen for window resize
        window.addEventListener('resize', updateWidth)

        return () => {
          window.removeEventListener('resize', updateWidth)
        }
      }
    }, [matchTriggerWidth, triggerRef])

    const mergedStyle = React.useMemo(() => {
      const baseStyle: React.CSSProperties = {}
      if (matchTriggerWidth && triggerWidth) {
        baseStyle.width = `${triggerWidth}px`
      }
      return { ...baseStyle, ...style }
    }, [matchTriggerWidth, triggerWidth, style])

    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={ref}
          align={align}
          side={side}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={collisionPadding}
          avoidCollisions={avoidCollisions}
          style={mergedStyle}
          className={cm(
            // Base styles - matches the Figma design
            'z-[9999] overflow-hidden rounded-[16px] bg-tokens-neutral-bg-white p-2 shadow-popover',
            // Smooth enter/exit animations
            'data-[state=open]:animate-dropdown-in',
            'data-[state=closed]:animate-dropdown-out',
            // Spacing between items
            'space-y-2',
            className
          )}
          {...props}
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    )
  }
)

DropdownMenuContent.displayName = 'DropdownMenuContent'

// Menu Item component with your Figma styles
interface DropdownMenuItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
    'children' | 'onSelect'
  > {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onClick?: (event: Event) => void
  onSelect?: (event: Event) => void
}

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  className = '',
  disabled = false,
  onClick,
  onSelect,
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Item
      disabled={disabled}
      onSelect={event => {
        onSelect?.(event)
        onClick?.(event)
      }}
      className={cm(
        // Base styles - matches the Figma design
        'relative flex w-full h-8 cursor-pointer select-none items-center justify-between',
        'rounded-[8px] pl-3 pr-2 text-base font-medium text-tokens-neutral-text-primary outline-none transition-colors leading-[22px] truncate',
        // State styles
        'focus:bg-tokens-neutral-bg-hover hover:bg-tokens-neutral-bg-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  )
}

// Separator component with your Figma styles
interface DropdownMenuSeparatorProps {
  className?: string
}

export const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({
  className = '',
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Separator
      className={cm('my-2 text-tokens-neutral-border-2', className)}
      {...props}
    >
      <svg
        width='100%'
        height='1'
        viewBox='0 0 100 1'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        preserveAspectRatio='none'
      >
        <path d='M0 0.5L100 0.5' stroke='currentColor' vectorEffect='non-scaling-stroke' />
      </svg>
    </DropdownMenuPrimitive.Separator>
  )
}

// Label component
interface DropdownMenuLabelProps {
  children: React.ReactNode
  className?: string
}

export const DropdownMenuLabel: React.FC<DropdownMenuLabelProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Label className={cm('px-3 py-1.5', className)} {...props}>
      <Typography
        fontFamily='geistMono'
        fontSize='bodyTag'
        className='text-tokens-neutral-text-tertiary'
        fontWeight='medium'
      >
        {children}
      </Typography>
    </DropdownMenuPrimitive.Label>
  )
}

// Sub menu components
export const DropdownMenuSub = DropdownMenuPrimitive.Sub

interface DropdownMenuSubTriggerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>,
    'children'
  > {
  children: React.ReactNode
  className?: string
  rightIcon?: React.ReactNode
}

export const DropdownMenuSubTrigger: React.FC<DropdownMenuSubTriggerProps> = ({
  children,
  className = '',
  rightIcon = (
    <SelectComponentRightCaretIcon className='ml-2 h-5 w-5 text-tokens-neutral-text-tertiary' />
  ),
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cm(
        'relative flex h-8 cursor-default select-none items-center justify-between',
        'rounded-[8px] px-3 text-sm outline-none transition-colors',
        'text-tokens-neutral-text-primary hover:bg-tokens-neutral-bg-hover',
        'focus:bg-tokens-neutral-bg-hover data-[state=open]:bg-tokens-neutral-bg-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span className='flex-1 text-left'>{children}</span>
      {rightIcon}
    </DropdownMenuPrimitive.SubTrigger>
  )
}

interface DropdownMenuSubContentProps {
  children: React.ReactNode
  className?: string
}

export const DropdownMenuSubContent: React.FC<DropdownMenuSubContentProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        className={cm(
          // Base styles - matches the Figma design
          'z-[9999] min-w-[8rem] overflow-hidden rounded-[12px] bg-tokens-neutral-bg-white p-2 shadow-popover',
          // Smooth enter/exit animations
          'data-[state=open]:animate-dropdown-in',
          'data-[state=closed]:animate-dropdown-out',
          // Spacing between items
          'space-y-2',
          className
        )}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.SubContent>
    </DropdownMenuPrimitive.Portal>
  )
}
